import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { Card } from '../components/ui/Card';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/Dialog';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatDateWithYear } from '../lib/format';
import { countryLabel, languageLabel } from '../lib/contactAttributes';
import {
  UNSUBSCRIBE_DETAIL_SOURCE_LABELS,
  UNSUBSCRIBE_REASON_LABELS,
} from '../lib/unsubscribeLabels';
import { ApiError, type UnsubscribeDetail } from '../types/api';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="m-0 text-right">{children}</dd>
    </div>
  );
}

/** Admin: clears the address from this unsubscribe and any other with it (GDPR art. 17). */
function RemoveAddressDialog({ id, email }: { id: string; email: string }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      apiFetch(`/suppressions/unsubscribes/${encodeURIComponent(id)}/email`, { method: 'DELETE' }),
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['suppressions'] });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) mutation.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          Remove address
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Remove the address"
        description={`${email} is removed from this and every other unsubscribe with it. It stays blocked: it is never mailed again and never re-added. Use this when someone asks to be forgotten.`}
      >
        {mutation.isError ? (
          <Alert>The address could not be removed. Please try again.</Alert>
        ) : null}
        <DialogFooter>
          <DialogClose>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button variant="danger" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Removing…' : 'Remove address'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Audience → Unsubscribed → one unsubscribe. The contact was deleted when
 * the person unsubscribed; this is what was kept, with the address for an
 * unsubscribe from a mail since 26 Oct 2026.
 */
export function AfmeldtPage() {
  const { id = '' } = useParams();
  const { isAdmin } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['suppressions', 'unsubscribe', id],
    queryFn: () =>
      apiFetch<UnsubscribeDetail>(`/suppressions/unsubscribes/${encodeURIComponent(id)}`),
  });

  const header = (
    <header className="flex items-center gap-3">
      <Link to="/afmeldte" className="no-underline">
        ← Unsubscribed
      </Link>
      <MockBadge />
    </header>
  );

  if (isLoading) return <div className="text-muted">Loading…</div>;
  if (error || !data) {
    return (
      <>
        {header}
        <Alert>
          {error instanceof ApiError && error.status === 404
            ? 'This unsubscribe was not found.'
            : 'The unsubscribe could not be loaded. Please try again shortly.'}
        </Alert>
      </>
    );
  }

  // Unsubscribes from before 26 Oct 2026 only kept when and how.
  const onlyWhenAndHow =
    !data.campaign && !data.language && !data.country && !data.signupSource && !data.subscribedOn;

  return (
    <>
      {header}
      <div className="flex flex-col gap-1">
        <h1 className="m-0 text-2xl font-semibold tracking-tight break-all">
          {data.email ?? `Unsubscribed on ${formatDateWithYear(data.occurredAt)}`}
        </h1>
        <p className="m-0 text-sm text-muted">
          {data.email
            ? `Unsubscribed on ${formatDateWithYear(data.occurredAt)}. The contact was deleted; this is what was kept.`
            : 'The address was not kept for this unsubscribe. This is what was.'}
        </p>
        {isAdmin && data.email ? (
          <div className="mt-2">
            <RemoveAddressDialog id={data.id} email={data.email} />
          </div>
        ) : null}
      </div>

      <Card className="p-4 max-w-[640px]">
        <dl className="m-0 flex flex-col gap-2 text-sm">
          <Row label="When">{formatDateTime(data.occurredAt)}</Row>
          <Row label="How">{UNSUBSCRIBE_DETAIL_SOURCE_LABELS[data.source]}</Row>
          <Row label="Reason">
            {data.reason ? (
              <>
                {UNSUBSCRIBE_REASON_LABELS[data.reason]}
                {data.reasonAt ? (
                  <span className="block text-xs text-muted">
                    given {formatDateTime(data.reasonAt)}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-muted">Not given</span>
            )}
          </Row>
          <Row label="Mail">
            {data.campaign ? (
              <Link to={`/kampagner/${data.campaign.id}`}>{data.campaign.name}</Link>
            ) : (
              '—'
            )}
          </Row>
          <Row label="Language">{languageLabel(data.language)}</Row>
          <Row label="Country">{countryLabel(data.country)}</Row>
          <Row label="Signup source">{data.signupSource ?? '—'}</Row>
          <Row label="Subscribed since">
            {data.subscribedOn ? formatDateWithYear(data.subscribedOn) : '—'}
          </Row>
        </dl>
      </Card>
      {onlyWhenAndHow ? (
        <p className="m-0 text-xs text-muted">
          This unsubscribe happened before the details were kept, so only when and how are known.
        </p>
      ) : null}
    </>
  );
}
